import { WINDOW_TOP_OFFSET } from '@/constants';
import type { IOpenWindowOptions, IWindowInstance } from '@/types/windowManager';

const windows = ref<IWindowInstance[]>([]);
const baseZIndex = 1000;
const windowOffset = 30;

const getComponentName = (component: Component): string => {
    if (typeof component === 'string') return component;

    const c = component as any;
    return c.name ?? c.__name ?? c.__file?.match(/([^/]+)\.vue$/)?.[1] ?? 'Window';
};

const hashObjectSync = (obj: any): string => {
    try {
        const json = JSON.stringify(obj);
        let hash = 0;
        for (let i = 0; i < json.length; i++) {
            const char = json.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(36).substring(0, 16);
    } catch (error) {
        return Math.random().toString(36).substring(2, 18);
    }
};

const hashObject = async (obj: any): Promise<string> => {
    if (typeof crypto !== 'undefined' && crypto.subtle && typeof crypto.subtle.digest === 'function') {
        try {
            const json = JSON.stringify(obj);
            const encoder = new TextEncoder();
            const data = encoder.encode(json);
            const hashBuffer = await crypto.subtle.digest('SHA-256', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
            return hashHex.substring(0, 16);
        } catch (error) {
            return hashObjectSync(obj);
        }
    }
    return hashObjectSync(obj);
};

export function useWindowManager() {
    const getMaxZIndex = (): number => {
        if (windows.value.length === 0) return baseZIndex;
        return Math.max(...windows.value.map((w) => w.zIndex), baseZIndex);
    };

    const activeWindowId = computed<string | null>(() => {
        const visibleWindows = windows.value.filter((w) => !w.isMinimized);
        if (visibleWindows.length === 0) return null;

        const maxZ = Math.max(...visibleWindows.map((w) => w.zIndex));
        return visibleWindows.find((w) => w.zIndex === maxZ)?.id ?? null;
    });

    const activateWindow = (windowId: string): void => {
        const window = windows.value.find((w) => w.id === windowId);
        if (window) {
            const maxZ = getMaxZIndex();
            window.isMinimized = false;
            window.zIndex = maxZ + 1;
        }
    };

    const minimizeWindow = (windowId: string): void => {
        const window = windows.value.find((w) => w.id === windowId);
        if (window) {
            window.isMinimized = true;
        }
    };

    const minimizeAllWindows = (): void => {
        windows.value.forEach((w) => {
            w.isMinimized = true;
        });
    };

    const toggleMinimize = (windowId: string): void => {
        const window = windows.value.find((w) => w.id === windowId);
        if (!window) return;

        if (!window.isMinimized && activeWindowId.value === windowId) {
            minimizeWindow(windowId);
        } else {
            activateWindow(windowId);
        }
    };

    const openWindow = async (params: IOpenWindowOptions): Promise<string> => {
        let uniqueKey = params.uniqueKey;

        if (!uniqueKey) {
            const componentName = getComponentName(params.component);
            const dataHash = await hashObject(params.data);
            uniqueKey = `${componentName}|${dataHash}`;
        }

        const allowMultiple = params.allowMultiple ?? false;

        if (!allowMultiple && uniqueKey) {
            const existingWindow = windows.value.find((w) => w.uniqueKey === uniqueKey);
            if (existingWindow) {
                activateWindow(existingWindow.id);
                return existingWindow.id;
            }
        }

        const windowId = `window-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
        const maxZ = getMaxZIndex();
        const index = windows.value.length;

        const defaultW = params.options?.width ?? window.innerWidth - 200;
        const defaultH = params.options?.height ?? window.innerHeight - 200;
        const defaultX =
            params.options?.x ??
            (() => {
                if (params.options?.width) {
                    return (window.innerWidth - params.options.width) / 2 + index * windowOffset;
                }
                return 100 + index * windowOffset;
            })();
        const defaultY = params.options?.y ?? WINDOW_TOP_OFFSET + index * windowOffset;

        const newWindow: IWindowInstance = {
            id: windowId,
            component: markRaw(params.component),
            data: params.data,
            uniqueKey: uniqueKey,
            x: defaultX,
            y: defaultY,
            w: defaultW,
            h: defaultH,
            zIndex: maxZ + 1,
            headerTitle: params.headerTitle ?? params.data?.headerTitle,
            headerIcon: params.headerIcon ?? params.data?.headerIcon ?? null,
            showFullscreenButton: params.showFullscreenButton ?? params.data?.showFullscreenButton ?? false,
            showFiltersButton: params.showFiltersButton ?? params.data?.showFiltersButton ?? false,
            showJsonToggleButton: params.showJsonToggleButton ?? params.data?.showJsonToggleButton ?? false,
        };

        windows.value.push(newWindow);
        return windowId;
    };

    const closeWindow = (windowId: string): void => {
        const index = windows.value.findIndex((w) => w.id === windowId);
        if (index !== -1) {
            windows.value.splice(index, 1);
            const newActive = activeWindowId.value;
            if (newActive) {
                activateWindow(newActive);
            }
        }
    };

    const closeAllWindows = (): void => {
        windows.value = [];
    };

    const updateWindowPosition = (windowId: string, x: number, y: number): void => {
        const window = windows.value.find((w) => w.id === windowId);
        if (window) {
            window.x = x;
            window.y = y;
        }
    };

    const updateWindowSize = (windowId: string, width: number, height: number): void => {
        const window = windows.value.find((w) => w.id === windowId);
        if (window) {
            window.w = width;
            window.h = height;
        }
    };

    const updateWindowHeaderTitle = (windowId: string, headerTitle: string): void => {
        const window = windows.value.find((w) => w.id === windowId);
        if (window) {
            window.headerTitle = headerTitle;
        }
    };

    const toggleFullscreen = (windowId: string): void => {
        const window = windows.value.find((w) => w.id === windowId);
        if (!window) return;

        if (window.isFullscreen) {
            window.x = window.savedX ?? window.x;
            window.y = window.savedY ?? window.y;
            window.w = window.savedW ?? window.w;
            window.h = window.savedH ?? window.h;
            window.isFullscreen = false;
        } else {
            window.savedX = window.x;
            window.savedY = window.y;
            window.savedW = window.w;
            window.savedH = window.h;

            window.x = 0;
            window.y = WINDOW_TOP_OFFSET;
            window.w = globalThis.innerWidth;
            window.h = globalThis.innerHeight - WINDOW_TOP_OFFSET;
            window.isFullscreen = true;
        }
    };

    return {
        windows: computed(() => windows.value),
        activeWindowId,
        openWindow,
        closeWindow,
        closeAllWindows,
        activateWindow,
        minimizeWindow,
        minimizeAllWindows,
        toggleMinimize,
        updateWindowPosition,
        updateWindowSize,
        updateWindowHeaderTitle,
        toggleFullscreen,
    };
}
